import { useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { format, isSaturday } from 'date-fns'
import { enUS, es } from 'date-fns/locale'

import { cn } from '@/shared/lib/styles/class-merge'
import { Calendar } from '@/shared/ui/calendar'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { DriverServiceSection } from '@/shared/ui/driver-service-section'
import { Separator } from '@/shared/ui/separator'
import { AdditionalService, DriverService } from '@monorepo/functions/src/types/services'

import { useHotelAvailability } from './HotelAvailabilityContext'

interface AvailabilityCalendarProps {
    className?: string
    selectedDates?: DateRange | null
    checkInTime: string
    checkOutTime: string
    onDatesChange: (dates: { from: Date; to: Date } | null) => void
    onCheckInTimeChange: (time: string) => void
    onCheckOutTimeChange: (time: string) => void
    onServiceChange: (services: AdditionalService[]) => void
    services?: AdditionalService[]
    showTimeErrors?: boolean
    isDriverTimeManagedExternally?: boolean
}

export function AvailabilityCalendar({
    className,
    selectedDates: parentSelectedDates,
    checkInTime,
    checkOutTime,
    onDatesChange,
    onCheckInTimeChange,
    onCheckOutTimeChange,
    onServiceChange,
    services = [],
    showTimeErrors = false,
    isDriverTimeManagedExternally = false,
}: AvailabilityCalendarProps) {
    const calendarDates: DateRange | undefined = parentSelectedDates?.from && parentSelectedDates?.to
        ? { from: new Date(parentSelectedDates.from), to: new Date(parentSelectedDates.to) }
        : undefined

    const [driverService, setDriverService] = useState<DriverService | undefined>(
        services.find(s => s.type === 'driver') as DriverService | undefined,
    )

    const { isWeekend, isHighSeason, isDateBlocked, isHoliday } = useHotelAvailability()

    const { t, i18n } = useTranslation()

    const calendarLocale = i18n.language === 'en' ? enUS : es

    const isRestrictedDay = (dateStr: string) => {
        return (isWeekend(dateStr) && !isSaturday(new Date(dateStr))) || isHoliday(dateStr)
    }

    const handleSelect = (range: DateRange | undefined) => {
        if (range?.from && isRestrictedDay(format(range.from, 'yyyy-MM-dd'))) {
            return
        }
        if (range?.to && isRestrictedDay(format(range.to, 'yyyy-MM-dd'))) {
            return
        }

        if (range?.from && range?.to) {
            const start = new Date(range.from)
            const end = new Date(range.to)

            for (let date = start; date <= end; date.setDate(date.getDate() + 1)) {
                if (isDateBlocked(format(date, 'yyyy-MM-dd'))) {
                    return
                }
            }
        }

        if (range?.from) {
            onDatesChange({ from: range.from, to: range.to || range.from })
        } else {
            onDatesChange(null)
        }
    }

    const handleDriverServiceChange = (newDriverService: DriverService | undefined) => {
        setDriverService(newDriverService)

        if (newDriverService) {
            onServiceChange([...services.filter(s => s.type !== 'driver'), newDriverService])
        } else {
            onServiceChange(services.filter(s => s.type !== 'driver'))
        }
    }

    return (
        <div className='w-full space-y-6'>
            <Card>
                <CardContent className='mt-4 space-y-6'>
                    <Calendar
                        fillSpace
                        mode='range'
                        selected={calendarDates}
                        onSelect={handleSelect}
                        className={cn('rounded-md border', className)}
                        defaultMonth={parentSelectedDates?.from ? new Date(parentSelectedDates.from) : undefined}
                        modifiers={{
                            available: date => {
                                return !isDateBlocked(format(date, 'yyyy-MM-dd'))
                            },
                            highSeason: date => {
                                const dateString = format(date, 'yyyy-MM-dd')
                                return isHighSeason(dateString)
                            },
                            weekend: date => {
                                const dateString = format(date, 'yyyy-MM-dd')
                                return isRestrictedDay(dateString)
                            },
                            saturday: date => isSaturday(date),
                            start: date =>
                                Boolean(
                                    calendarDates?.from &&
                                        format(date, 'yyyy-MM-dd') === format(calendarDates.from, 'yyyy-MM-dd'),
                                ),
                            end: date =>
                                Boolean(
                                    calendarDates?.to &&
                                        format(date, 'yyyy-MM-dd') === format(calendarDates.to, 'yyyy-MM-dd'),
                                ),
                            range: date =>
                                Boolean(
                                    calendarDates?.from &&
                                        calendarDates?.to &&
                                        date > calendarDates.from &&
                                        date < calendarDates.to,
                                ),
                        }}
                        modifiersStyles={{
                            highSeason: { backgroundColor: '#fbbf24', color: 'white' },
                            weekend: { backgroundColor: 'red', color: 'white', cursor: 'not-allowed' },
                            start: { backgroundColor: '#7D8F53', color: 'white', fontWeight: 'bold' },
                            end: { backgroundColor: '#7D8F53', color: 'white', fontWeight: 'bold' },
                            range: {
                                backgroundColor: 'rgba(125, 143, 83, 0.5)',
                            },
                        }}
                        disabled={date => {
                            const dateString = format(date, 'yyyy-MM-dd')
                            return isRestrictedDay(dateString) || isDateBlocked(dateString)
                        }}
                        fromDate={new Date()}
                        locale={calendarLocale}
                    />
                    <div className='flex flex-wrap justify-center gap-4'>
                        <div className='flex items-center'>
                            <div className='mr-2 h-4 w-4 rounded bg-[#fbbf24]'></div>
                            <span className='text-sm'>{t('booking.step2.calendar.highSeason')}</span>
                        </div>
                        <div className='flex items-center'>
                            <div className='mr-2 h-4 w-4 rounded bg-red-500'></div>
                            <span className='text-sm'>{t('booking.step2.calendar.holidays')}</span>
                        </div>
                        <div className='flex items-center'>
                            <div className='mr-2 h-4 w-4 rounded bg-gray-200'></div>
                            <span className='text-sm'>{t('booking.step2.calendar.notAvailable')}</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>{t('booking.step2.services.title')}</CardTitle>
                </CardHeader>
                <CardContent className='space-y-6'>
                    <DriverServiceSection
                        driverService={driverService}
                        onDriverServiceChange={handleDriverServiceChange}
                        checkInTime={checkInTime}
                        checkOutTime={checkOutTime}
                        onCheckInTimeChange={onCheckInTimeChange}
                        onCheckOutTimeChange={onCheckOutTimeChange}
                        selectedDates={
                            calendarDates?.from && calendarDates?.to
                                ? { from: calendarDates.from, to: calendarDates.to }
                                : null
                        }
                        showTimeErrors={showTimeErrors}
                        isDriverTimeManagedExternally={isDriverTimeManagedExternally}
                    />

                    <Separator />
                </CardContent>
            </Card>
        </div>
    )
}
